import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaBars, 
  FaTimes, 
  FaUser, 
  FaCog, 
  FaSignOutAlt, 
  FaBell,
  FaHome,
  FaUsers,
  FaChartLine,
  FaCog as FaSettings,
  FaShieldAlt,
  FaFileAlt,
  FaTachometerAlt,
  FaMapMarkerAlt,
  FaClipboardList
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import InactivityTimer from '../InactivityTimer';
import InactivityWarning from '../InactivityWarning';

const Layout = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ✅ Use inactivity timer with countdown and warning
  const {
    timeLeft,
    showWarning,
    formatTime,
    handleContinue,
    handleLogout,
    setIsActive
  } = useInactivityTimer(logout, 240000); // 4 minutes

  // Pause timer when sidebar is open or user is interacting
  React.useEffect(() => {
    setIsActive(!sidebarOpen);
  }, [sidebarOpen, setIsActive]);

  if (loading) return <div>Loading layout...</div>;

  const getNavigationItems = () => {
    const mainRole = user?.main_role;
    const subRoles = user?.sub_roles || [];

    const baseItems = [
      { name: 'Dashboard', icon: <FaTachometerAlt />, href: '/dashboard' },
      { name: 'Wallet', icon: <FaFileAlt />, href: '/wallet' }, // Added Wallet link
    ];

    switch (mainRole) {
      case 'admin':
        return [
          ...baseItems,
          { name: 'User Management', icon: <FaUsers />, href: '/admin/users' },
          { name: 'System Settings', icon: <FaSettings />, href: '/admin/settings' },
          { name: 'Reports & Analytics', icon: <FaChartLine />, href: '/admin/reports' },
          { name: 'Login Logs', icon: <FaShieldAlt />, href: '/admin/login-logs' },
          { name: 'Security Logs', icon: <FaShieldAlt />, href: '/admin/security' },
          { name: 'System Health', icon: <FaHome />, href: '/admin/health' }
        ];
      
      case 'distributor':
        return [
          ...baseItems,
          { name: 'Regional Management', icon: <FaMapMarkerAlt />, href: '/distributor/regional' },
          { name: 'Sales Tracking', icon: <FaChartLine />, href: '/distributor/sales' },
          { name: 'Team Management', icon: <FaUsers />, href: '/distributor/team' },
          { name: 'Reports', icon: <FaFileAlt />, href: '/distributor/reports' },
          { name: 'Login Logs', icon: <FaShieldAlt />, href: '/distributor/login-logs' },
          { name: 'Settings', icon: <FaSettings />, href: '/distributor/settings' }
        ];
      
      case 'user':
        const userItems = [
          ...baseItems,
          { name: 'Login Logs', icon: <FaShieldAlt />, href: '/user/login-logs' },
          { name: 'Profile', icon: <FaUser />, href: '/user/profile' },
          { name: 'Tasks', icon: <FaClipboardList />, href: '/user/tasks' },
          { name: 'Reports', icon: <FaFileAlt />, href: '/user/reports' },
          { name: 'Settings', icon: <FaSettings />, href: '/user/settings' }
        ];

        // Add sub-role specific items
        if (subRoles.includes('sales')) {
          userItems.push({ name: 'Sales Dashboard', icon: <FaChartLine />, href: '/user/sales' });
        }
        if (subRoles.includes('office')) {
          userItems.push({ name: 'Office Tasks', icon: <FaFileAlt />, href: '/user/office' });
        }
        if (subRoles.includes('accounts')) {
          userItems.push({ name: 'Accounts', icon: <FaChartLine />, href: '/user/accounts' });
        }
        if (subRoles.includes('approval_team')) {
          userItems.push({ name: 'Approvals', icon: <FaShieldAlt />, href: '/user/approvals' });
        }

        return userItems;
      
      default:
        return baseItems;
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div>
        {/* Page content */}
        <main>
          {children}
        </main>
      </div>

      {/* Inactivity Warning Popup */}
      <InactivityWarning 
        showWarning={showWarning}
        handleContinue={handleContinue}
        handleLogout={handleLogout}
      />
    </div>
  );
};

export default Layout; 